import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { getApiError } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import useValuatorForm from "@/hooks/useValuatorForm";
import usePhotoUpload from "@/hooks/usePhotoUpload";
import ValuatorStep1 from "@/components/valuator/ValuatorStep1";
import ValuatorStep2 from "@/components/valuator/ValuatorStep2";
import ValuatorStep3 from "@/components/valuator/ValuatorStep3";
import MobileActionBar from "@/components/valuator/MobileActionBar";

const Valuator = () => {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const vf = useValuatorForm();
  const photo = usePhotoUpload();

  const [step, setStep] = useState(1);
  const [maxStep, setMaxStep] = useState(1);
  const [quickRange, setQuickRange] = useState(null);
  const [quickLoading, setQuickLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const goToStep = (n) => { setStep(n); setMaxStep((m) => Math.max(m, n)); };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  const onCategoryChange = (value) => {
    vf.onCategoryChange(value);
    setQuickRange(null);
  };

  const buildPayload = () => vf.buildPayload(photo.photos.map((p) => p.path), lang);

  // Guard against out-of-range age (e.g. user typed a year like "2020"):
  // the backend caps age at 1200 months and would otherwise 422.
  const isPayloadValid = (payload) => {
    if (Number(payload.age_months) > 1200) {
      toast.error(t.valuator.age_too_high);
      return false;
    }
    return true;
  };

  const fetchQuickRange = async () => {
    const payload = buildPayload();
    if (!isPayloadValid(payload)) return;
    setQuickLoading(true);
    try {
      const res = await api.post("/valuation/quick", payload);
      setQuickRange(res.data);
    } catch (err) {
      toast.error(getApiError(err));
    } finally {
      setQuickLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = buildPayload();
    if (!isPayloadValid(payload)) return;
    setLoading(true);
    try {
      const res = await api.post("/valuation/calculate", payload);
      sessionStorage.setItem("rv_last_result", JSON.stringify({
        input: payload,
        result: res.data,
        photo_previews: photo.photos.map((p) => p.preview),
      }));
      navigate("/risultato");
    } catch (err) {
      toast.error(getApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { n: 1, label: t.valuator.section_basics },
    { n: 2, label: t.valuator.section_details },
    { n: 3, label: t.valuator.section_extras },
  ];

  const hasPrice = parseFloat(vf.form.purchase_price) > 0 || parseFloat(vf.form.original_retail_price) > 0;
  const step1Valid = hasPrice && (!vf.isVehicle || vf.form.kilometers);

  return (
    <main data-testid="valuator-page" className="bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 pt-14 pb-32 lg:pb-14">
        <span className="overline text-[#002FA7]">PRICING / WIZARD</span>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-2">{t.valuator.title}</h1>
        <p className="mt-4 text-[#4A4A4A] text-lg max-w-2xl">{t.valuator.sub}</p>

        {/* STEP INDICATOR */}
        <div data-testid="step-indicator" className="mt-8 flex items-stretch border border-black divide-x divide-black">
          {steps.map((s) => (
            <button
              key={s.n}
              type="button"
              data-testid={`step-tab-${s.n}`}
              onClick={() => s.n !== step && s.n <= maxStep && setStep(s.n)}
              disabled={s.n > maxStep}
              aria-disabled={s.n > maxStep}
              className={`flex-1 px-3 py-3 text-left transition-colors ${step === s.n ? "bg-[#0A0A0A] text-white" : s.n <= maxStep ? "bg-[#FFCC00]/30 hover:bg-[#FFCC00]/50 cursor-pointer" : "bg-white text-[#737373] cursor-default"}`}
            >
              <span className="font-mono text-[10px] font-bold tracking-widest uppercase">0{s.n} / {s.label}</span>
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="mt-8">
          {step === 1 && (
            <ValuatorStep1
              vf={vf}
              onCategoryChange={onCategoryChange}
              quickRange={quickRange}
              quickLoading={quickLoading}
              onQuickRange={fetchQuickRange}
              step1Valid={step1Valid}
              onNext={() => goToStep(2)}
            />
          )}

          {step === 2 && (
            <ValuatorStep2 vf={vf} onBack={() => setStep(1)} onNext={() => goToStep(3)} />
          )}

          {step === 3 && (
            <ValuatorStep3 vf={vf} photo={photo} loading={loading} onBack={() => setStep(2)} />
          )}

          <MobileActionBar
            step={step}
            quickRange={!!quickRange}
            step1Valid={step1Valid}
            quickLoading={quickLoading}
            loading={loading}
            uploading={photo.uploading}
            canSubmit={hasPrice}
            onBack={() => setStep(Math.max(1, step - 1))}
            onQuickRange={fetchQuickRange}
            onGotoStep2={() => goToStep(2)}
            onGotoStep3={() => goToStep(3)}
          />
        </form>
      </div>
    </main>
  );
};

export default Valuator;
